// <s-hair-quiz> — step-by-step hair diagnosis that scores each answer and routes to the best-matching collection.
// Markup contract: [data-step] fieldsets with radio inputs carrying data-score="handle:points,…",
// a [data-result] panel, [data-step-label]/[data-progress] indicators, and a JSON <script data-collections>.
class SHairQuiz extends HTMLElement {
  #ac;
  #steps = [];
  #index = 0;
  #collections = {};
  #announceRaf = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.#steps = [...this.querySelectorAll('[data-step]')];
    try {
      this.#collections = JSON.parse(this.querySelector('script[data-collections]')?.textContent || '{}');
    } catch {
      this.#collections = {};
    }

    this.addEventListener('change', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
      // Brief pause lets the selected state register visually before the step changes.
      window.setTimeout(() => this.#next(), this.#reducedMotion() ? 0 : 180);
    }, opts);

    this.addEventListener('command', (event) => {
      if (event.command === '--back') this.#show(Math.max(0, this.#index - 1));
      if (event.command === '--restart') this.#restart();
    }, opts);

    this.#show(0, false);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#announceRaf);
  }

  #reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #next() {
    if (this.#index < this.#steps.length - 1) {
      this.#show(this.#index + 1);
    } else {
      this.#finish();
    }
  }

  #show(index, moveFocus = true) {
    this.#index = index;
    this.#steps.forEach((step, i) => { step.hidden = i !== index; });
    const result = this.querySelector('[data-result]');
    if (result) result.hidden = true;
    this.querySelector('[data-quiz-progress]')?.removeAttribute('hidden');

    const total = this.#steps.length;
    const label = this.querySelector('[data-step-label]');
    const template = this.dataset.stepTemplate || '[current] / [total]';
    if (label) label.textContent = template.replace('[current]', index + 1).replace('[total]', total);
    const bar = this.querySelector('[data-progress]');
    if (bar) bar.style.inlineSize = `${((index + 1) / total) * 100}%`;
    const back = this.querySelector('[data-back]');
    if (back) back.hidden = index === 0;

    if (moveFocus) {
      // The answered step was hidden, so focus moves to the new question heading.
      this.#steps[index].querySelector('[data-step-heading]')?.focus({ preventScroll: true });
      this.#announce(label?.textContent || '');
    }
  }

  #finish() {
    const scores = {};
    const order = [];
    for (const input of this.querySelectorAll('input[type="radio"]:checked')) {
      for (const pair of (input.dataset.score || '').split(',')) {
        const [handle, points] = pair.split(':').map((part) => part.trim());
        if (!handle) continue;
        scores[handle] = (scores[handle] || 0) + Number(points || 1);
        if (!order.includes(handle)) order.push(handle);
      }
    }
    // Ties resolve to the most recent answer's collection, so the final "priority" question wins.
    let best = this.dataset.fallback || '';
    let bestScore = -1;
    for (const handle of order) {
      if (scores[handle] >= bestScore && this.#collections[handle]) {
        best = handle;
        bestScore = scores[handle];
      }
    }
    const match = this.#collections[best] || this.#collections[this.dataset.fallback] || null;

    this.#steps.forEach((step) => { step.hidden = true; });
    this.querySelector('[data-quiz-progress]')?.setAttribute('hidden', '');
    const back = this.querySelector('[data-back]');
    if (back) back.hidden = true;

    const result = this.querySelector('[data-result]');
    if (!result) return;
    const title = result.querySelector('[data-result-title]');
    const link = result.querySelector('[data-result-link]');
    if (match) {
      if (title) title.textContent = match.title;
      if (link) link.setAttribute('href', match.url);
    }
    result.hidden = false;
    result.querySelector('[data-result-heading]')?.focus({ preventScroll: true });
    this.#announce(`${result.querySelector('[data-result-heading]')?.textContent || ''} ${match?.title || ''}`.trim());
  }

  #restart() {
    for (const input of this.querySelectorAll('input[type="radio"]')) input.checked = false;
    this.#show(0);
  }

  #announce(text) {
    const region = this.querySelector('[data-quiz-status]');
    if (!region) return;
    cancelAnimationFrame(this.#announceRaf);
    region.textContent = '';
    this.#announceRaf = requestAnimationFrame(() => { region.textContent = text; });
  }
}

if (!customElements.get('s-hair-quiz')) customElements.define('s-hair-quiz', SHairQuiz);
